/* Falco Racing — comportements communs. Modules ES, sans dépendance. */

const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function formatMoney(cents, format = window.Falco?.moneyFormat || "{{amount_with_comma_separator}} €") {
  const value = (Number(cents) || 0) / 100;
  const fr = (d) => value.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d });
  const m = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const key = m ? m[1] : "amount";
  let out;
  switch (key) {
    case "amount_no_decimals": case "amount_no_decimals_with_comma_separator": out = fr(0); break;
    case "amount_with_comma_separator": out = fr(2).replace(/\s/g, "."); break;
    case "amount_with_space_separator": out = fr(2); break;
    case "amount_no_decimals_with_space_separator": out = fr(0); break;
    case "amount_with_apostrophe_separator": out = fr(2); break;
    default: out = value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return format.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]+>/g, "").trim();
}

let locks = 0;
function lockScroll(on) {
  locks = Math.max(0, locks + (on ? 1 : -1));
  document.body.style.overflow = locks > 0 ? "hidden" : "";
}

/* ---------- Révélation à l'entrée dans l'écran ---------- */
const revealObs = new IntersectionObserver((entries) => {
  entries.forEach((e) => {
    if (e.isIntersecting) { e.target.dataset.visible = "true"; revealObs.unobserve(e.target); }
  });
}, { rootMargin: "0px 0px -12% 0px", threshold: 0.06 });
function watchReveals(root = document) {
  root.querySelectorAll(".reveal:not([data-visible])").forEach((el) => { el.dataset.visible = "false"; revealObs.observe(el); });
}
watchReveals();
new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && watchReveals(n))))
  .observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener("shopify:section:load", (e) => watchReveals(e.target));

/* ---------- Accordéons (fold) ---------- */
document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-fold-trigger]");
  if (!btn) return;
  const panel = document.getElementById(btn.getAttribute("aria-controls"));
  if (!panel) return;
  const open = panel.dataset.open === "true";
  const group = btn.closest("[data-fold-group]");
  if (group && !open) {
    group.querySelectorAll("[data-fold-trigger][aria-expanded=true]").forEach((b) => {
      b.setAttribute("aria-expanded", "false");
      const p = document.getElementById(b.getAttribute("aria-controls"));
      if (p) p.dataset.open = "false";
      b.querySelector("[data-fold-icon]")?.classList.replace("bg-violet", "bg-white");
      b.querySelector("[data-fold-icon]")?.classList.replace("text-white", "text-graphite");
      b.querySelector("[data-icon-plus]")?.removeAttribute("hidden");
      b.querySelector("[data-icon-minus]")?.setAttribute("hidden", "");
    });
  }
  panel.dataset.open = open ? "false" : "true";
  btn.setAttribute("aria-expanded", open ? "false" : "true");
  const icon = btn.querySelector("[data-fold-icon]");
  if (icon) {
    icon.classList.toggle("bg-violet", !open); icon.classList.toggle("text-white", !open);
    icon.classList.toggle("bg-white", open); icon.classList.toggle("text-graphite", open);
  }
  btn.querySelector("[data-icon-plus]")?.toggleAttribute("hidden", !open);
  btn.querySelector("[data-icon-minus]")?.toggleAttribute("hidden", open);
  const card = btn.closest("[data-fold-card]");
  if (card) { card.classList.toggle("bg-papier", !open); card.classList.toggle("bg-papier/60", open); }
});

/* ---------- Bandeau d'annonce ---------- */
class FalcoAnnouncement extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll("[data-slide]")];
    this.i = 0;
    this.querySelector("[data-prev]")?.addEventListener("click", () => this.go(-1));
    this.querySelector("[data-next]")?.addEventListener("click", () => this.go(1));
    this.arm();
  }
  disconnectedCallback() { clearInterval(this.t); }
  arm() {
    clearInterval(this.t);
    if (reduced() || this.slides.length < 2) return;
    this.t = setInterval(() => this.go(1), Number(this.dataset.interval) || 4000);
  }
  go(dir) {
    const n = this.slides.length;
    this.i = (this.i + dir + n) % n;
    this.slides.forEach((s, k) => {
      const active = k === this.i;
      const passed = (k - this.i + n) % n > n / 2;
      s.classList.toggle("opacity-100", active); s.classList.toggle("opacity-0", !active);
      s.classList.toggle("translate-y-0", active);
      s.classList.toggle("-translate-y-2", !active && passed);
      s.classList.toggle("translate-y-2", !active && !passed);
      s.setAttribute("aria-hidden", active ? "false" : "true");
    });
    this.arm();
  }
}
customElements.define("falco-announcement", FalcoAnnouncement);

/* ---------- En-tête : fond, méga-menu, tiroir mobile ---------- */
class FalcoHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector("header");
    this.transparent = this.dataset.transparent === "true";
    this.megaOpen = false; this.mega = null; this.drawer = null;
    const onScroll = () => { this.scrolled = window.scrollY > 24; this.sync(); };
    window.addEventListener("scroll", onScroll, { passive: true }); onScroll();
    const stack = this.closest("#en-tete") || this;
    const setStack = () => document.documentElement.style.setProperty("--hdr", `${stack.offsetHeight}px`);
    new ResizeObserver(setStack).observe(stack); setStack();

    const trigger = this.querySelector("[data-mega-trigger]");
    trigger?.addEventListener("mouseenter", () => this.openMega());
    trigger?.querySelector("a")?.addEventListener("focus", () => this.openMega());
    trigger?.querySelector("[data-mega-toggle]")?.addEventListener("click", () => (this.megaOpen ? this.closeMega() : this.openMega()));
    this.querySelectorAll("[data-mega-leave]").forEach((a) => a.addEventListener("mouseenter", () => this.scheduleClose()));
    this.header.addEventListener("mouseleave", () => this.scheduleClose());

    this.querySelector("[data-open-drawer]")?.addEventListener("click", () => this.openDrawer());
    this.querySelector("[data-open-search]")?.addEventListener("click", () => this.querySelector("falco-search")?.open());
    this.querySelector("[data-open-cart]")?.addEventListener("click", () => document.querySelector("falco-cart-drawer")?.open());
    window.addEventListener("keydown", (e) => { if (e.key === "Escape") { this.closeMega(); this.closeDrawer(); } });
  }
  sync() {
    const solid = !this.transparent || this.scrolled || this.megaOpen;
    this.dataset.solid = String(solid);
    this.dataset.mega = String(this.megaOpen);
  }
  mountMega() {
    if (this.mega) return;
    const tpl = this.querySelector("[data-mega-template]");
    this.mega = tpl.content.firstElementChild.cloneNode(true);
    this.header.appendChild(this.mega);
    this.mega.addEventListener("mouseenter", () => this.openMega());
    this.mega.addEventListener("mouseleave", () => this.scheduleClose());
    this.mega.querySelectorAll("a").forEach((a) => a.addEventListener("click", () => this.closeMega()));
  }
  openMega() {
    if (window.innerWidth < 1024) return;
    this.mountMega(); clearTimeout(this.closeT);
    this.mega.hidden = false;
    requestAnimationFrame(() => {
      this.mega.classList.remove("pointer-events-none", "-translate-y-2", "opacity-0");
      this.mega.classList.add("pointer-events-auto", "translate-y-0", "opacity-100");
    });
    this.megaOpen = true; this.querySelector("[data-mega-toggle]")?.setAttribute("aria-expanded", "true"); this.sync();
  }
  closeMega() {
    if (!this.mega || !this.megaOpen) return;
    this.mega.classList.add("pointer-events-none", "-translate-y-2", "opacity-0");
    this.mega.classList.remove("pointer-events-auto", "translate-y-0", "opacity-100");
    setTimeout(() => { if (!this.megaOpen) this.mega.hidden = true; }, 300);
    this.megaOpen = false; this.querySelector("[data-mega-toggle]")?.setAttribute("aria-expanded", "false"); this.sync();
  }
  scheduleClose() { clearTimeout(this.closeT); this.closeT = setTimeout(() => this.closeMega(), 140); }
  mountDrawer() {
    if (this.drawer) return;
    const tpl = this.querySelector("[data-drawer-template]");
    this.drawer = tpl.content.firstElementChild.cloneNode(true);
    this.appendChild(this.drawer);
    this.drawer.querySelectorAll("[data-drawer-close]").forEach((b) => b.addEventListener("click", () => this.closeDrawer()));
    this.drawer.querySelectorAll("nav a, [data-drawer-panel] > div:last-child a").forEach((a) => a.addEventListener("click", () => this.closeDrawer()));
    const toggle = this.drawer.querySelector("[data-drawer-shop-toggle]");
    const fold = this.drawer.querySelector("[data-drawer-shop]");
    toggle?.addEventListener("click", () => {
      const open = fold.dataset.open === "true";
      fold.dataset.open = String(!open);
      toggle.setAttribute("aria-expanded", String(!open));
      toggle.querySelector("svg")?.classList.toggle("rotate-180", !open);
    });
    watchReveals(this.drawer);
  }
  openDrawer() {
    this.mountDrawer();
    const d = this.drawer, panel = d.querySelector("[data-drawer-panel]"), veil = d.querySelector("[data-drawer-close]");
    d.classList.remove("pointer-events-none", "invisible"); d.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => { panel.classList.remove("translate-x-full"); panel.classList.add("translate-x-0"); veil.classList.replace("opacity-0", "opacity-100"); });
    veil.tabIndex = 0; lockScroll(true);
    this.querySelector("[data-open-drawer]")?.setAttribute("aria-expanded", "true");
    setTimeout(() => d.querySelector("[data-drawer-close]:not([tabindex='-1'])")?.focus(), 300);
  }
  closeDrawer() {
    if (!this.drawer || this.drawer.classList.contains("invisible")) return;
    const d = this.drawer, panel = d.querySelector("[data-drawer-panel]"), veil = d.querySelector("[data-drawer-close]");
    panel.classList.add("translate-x-full"); panel.classList.remove("translate-x-0"); veil.classList.replace("opacity-100", "opacity-0");
    setTimeout(() => { d.classList.add("pointer-events-none", "invisible"); d.setAttribute("aria-hidden", "true"); }, 400);
    veil.tabIndex = -1; lockScroll(false);
    this.querySelector("[data-open-drawer]")?.setAttribute("aria-expanded", "false");
  }
}
customElements.define("falco-header", FalcoHeader);

/* ---------- Recherche prédictive ---------- */
class FalcoSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("input[type=search]");
    this.idle = this.querySelector("[data-search-idle]");
    this.results = this.querySelector("[data-search-results]");
    this.empty = this.querySelector("[data-search-empty]");
    this.querySelectorAll("[data-search-close]").forEach((b) => b.addEventListener("click", () => this.close()));
    this.input.addEventListener("input", () => { clearTimeout(this.t); this.t = setTimeout(() => this.query(), 180); });
    window.addEventListener("keydown", (e) => e.key === "Escape" && this.close());
  }
  open() {
    this.dataset.open = "true"; this.setAttribute("aria-hidden", "false"); lockScroll(true);
    this.querySelector("[data-search-close]").tabIndex = 0;
    this.input.value = ""; this.show("idle");
    setTimeout(() => this.input.focus(), 60);
  }
  close() {
    if (this.dataset.open !== "true") return;
    this.dataset.open = "false"; this.setAttribute("aria-hidden", "true"); lockScroll(false);
    this.querySelector("[data-search-close]").tabIndex = -1;
  }
  show(which) {
    this.idle.hidden = which !== "idle"; this.results.hidden = which !== "results"; this.empty.hidden = which !== "empty";
  }
  async query() {
    const q = this.input.value.trim();
    if (q.length < 2) return this.show("idle");
    const url = `${window.Falco.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=10&resources[options][unavailable_products]=last`;
    let data;
    try { data = await (await fetch(url, { headers: { Accept: "application/json" } })).json(); } catch { return; }
    if (this.input.value.trim() !== q) return;
    // Produits techniques (suppléments ajoutés par le configurateur, tag « masque ») : jamais proposés à la recherche.
    const visible = (p) => !(p.tags || []).some((t) => String(t).toLowerCase() === "masque") && p.type !== "Supplément";
    const prods = (data.resources?.results?.products || []).filter(visible).slice(0, 8), cols = (data.resources?.results?.collections || []).slice(0, 5);
    if (!prods.length && !cols.length) return this.show("empty");
    const shell = this.querySelector("[data-result-shell]").content.cloneNode(true);
    const cats = shell.querySelector("[data-cats]"), pr = shell.querySelector("[data-prods]");
    if (cols.length) {
      cats.hidden = false;
      cols.forEach((c) => { const li = this.querySelector("[data-result-collection]").content.cloneNode(true); const a = li.querySelector("a"); a.href = c.url; a.textContent = c.title; cats.querySelector("ul").appendChild(li); });
    }
    if (prods.length) {
      pr.hidden = false;
      prods.forEach((p) => {
        const li = this.querySelector("[data-result-product]").content.cloneNode(true);
        li.querySelector("a").href = p.url;
        const img = li.querySelector("img");
        if (p.featured_image?.url) { img.src = p.featured_image.url.replace(/(\.[a-z]+)(\?.*)?$/i, "_112x112$1$2"); img.alt = p.title; } else img.remove();
        li.querySelector("[data-title]").textContent = p.title;
        li.querySelector("[data-sub]").textContent = p.type || p.vendor || "";
        li.querySelector("[data-price]").textContent = formatMoney(Number(p.price) * 100);
        pr.querySelector("ul").appendChild(li);
      });
    }
    shell.querySelector("[data-all]").href = `${window.Falco.routes.search}?q=${encodeURIComponent(q)}`;
    this.results.replaceChildren(shell); this.show("results");
  }
}
customElements.define("falco-search", FalcoSearch);

/* ---------- Panier : tiroir, AJAX, réservation ---------- */
const HOLD_KEY = "falco-hold-until";
const OPEN_KEY = "falco-cart-open";
// Page traduite par Google Translate (app Uplinkly) : le contenu du tiroir remplacé après un ajout n'est pas
// toujours retraduit, d'où l'ouverture avant rafraîchissement et le rechargement de secours (verifierTraduction).
const pageTraduite = () => /\btranslated-(ltr|rtl)\b/.test(document.documentElement.className);
class FalcoCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bind();
    window.addEventListener("keydown", (e) => e.key === "Escape" && this.close());
    document.addEventListener("submit", (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute("data-ajax-cart")) return;
      e.preventDefault(); this.addFromForm(form);
    });
    this.tick = this.tick.bind(this); this.startClock();
    if (sessionStorage.getItem(OPEN_KEY)) { sessionStorage.removeItem(OPEN_KEY); setTimeout(() => this.open(), 400); }
  }
  bind() {
    this.querySelectorAll("[data-cart-close]").forEach((b) => b.addEventListener("click", () => this.close()));
    this.querySelectorAll("[data-cart-qty]").forEach((b) => b.addEventListener("click", () => {
      const line = Number(b.dataset.cartQty), delta = Number(b.dataset.delta);
      const qtyEl = b.parentElement.querySelector("span");
      this.change(line, Math.max(0, Number(qtyEl.textContent) + delta));
    }));
    this.querySelectorAll("[data-cart-remove]").forEach((b) => b.addEventListener("click", () => this.change(Number(b.dataset.cartRemove), 0)));
    this.querySelector("[data-hold-renew]")?.addEventListener("click", () => { this.renew(); this.tick(); });
  }
  open() {
    if (this.dataset.open === "true") return;
    this.dataset.open = "true"; this.setAttribute("aria-hidden", "false"); lockScroll(true);
    const veil = this.querySelector("[data-cart-close]"); if (veil) veil.tabIndex = 0;
    setTimeout(() => this.querySelector("[role=dialog] [data-cart-close]:not([tabindex='-1'])")?.focus(), 350);
  }
  close() {
    if (this.dataset.open !== "true") return;
    this.dataset.open = "false"; this.setAttribute("aria-hidden", "true"); lockScroll(false);
    const veil = this.querySelector("[data-cart-close]"); if (veil) veil.tabIndex = -1;
  }
  async addFromForm(form) {
    const btn = form.querySelector("[type=submit]");
    const fd = new FormData(form);
    fd.append("sections", this.sectionId); fd.append("sections_url", window.location.pathname);
    btn?.setAttribute("aria-busy", "true");
    try {
      const res = await fetch(window.Falco.routes.cartAdd + ".js", { method: "POST", body: fd, headers: { Accept: "application/json" } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message);
      // Un supplément ne part qu'une fois par configuration : têtes inox ou NéoChrome choisies en haut ET en bas
      // sur une bicolore = un seul supplément (demande Elvyn).
      const extras = form.querySelectorAll("[data-addon-id]"), seen = new Set();
      for (const ex of extras) {
        if (ex.dataset.addonActive !== "true" || !ex.dataset.addonId || seen.has(ex.dataset.addonId)) continue;
        seen.add(ex.dataset.addonId);
        await fetch(window.Falco.routes.cartAdd + ".js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: Number(ex.dataset.addonId), quantity: Number(fd.get("quantity") || 1), properties: { _pour: form.dataset.productTitle || "" } }) });
      }
      this.renew();
      if (pageTraduite()) this.open();
      await this.refresh();
      this.open();
      form.dispatchEvent(new CustomEvent("falco:added", { bubbles: true, detail: data }));
    } catch (err) {
      form.dispatchEvent(new CustomEvent("falco:add-error", { bubbles: true, detail: err }));
      console.error("Ajout au panier impossible :", err);
    } finally { btn?.removeAttribute("aria-busy"); }
  }
  async change(line, quantity) {
    const res = await fetch(window.Falco.routes.cartChange + ".js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ line, quantity }) });
    if (res.ok) await this.refresh();
  }
  async refresh() {
    const res = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
    const json = await res.json();
    const html = json[this.sectionId];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const fresh = doc.querySelector("[data-cart-body]");
    this.querySelector("[data-cart-body]").replaceWith(fresh);
    this.bind(); this.tick();
    const cart = await (await fetch(window.Falco.routes.cart + ".js", { headers: { Accept: "application/json" } })).json();
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle("bg-violet", cart.item_count > 0); el.classList.toggle("bg-gris-3", cart.item_count === 0);
    });
    if (cart.item_count === 0) localStorage.removeItem(HOLD_KEY);
    document.dispatchEvent(new CustomEvent("falco:cart", { detail: cart }));
    this.verifierTraduction();
  }
  // Page traduite et tiroir resté en français 1,5 s après le rafraîchissement : on recharge (Google Translate
  // traduit toute la page au chargement) et le tiroir se rouvre.
  verifierTraduction() {
    if (!pageTraduite()) return;
    setTimeout(() => {
      const body = this.querySelector("[data-cart-body]");
      if (!body || body.querySelector("font")) return;
      sessionStorage.setItem(OPEN_KEY, "1");
      window.location.reload();
    }, 1500);
  }
  renew() {
    if (!window.Falco.cartHoldEnabled) return;
    localStorage.setItem(HOLD_KEY, String(Date.now() + (window.Falco.cartHoldMinutes || 10) * 60 * 1000));
  }
  startClock() { clearInterval(this.clock); this.clock = setInterval(this.tick, 1000); this.tick(); }
  tick() {
    const box = this.querySelector("[data-hold]");
    if (!box) return;
    let until = Number(localStorage.getItem(HOLD_KEY) || 0);
    if (!until) { this.renew(); until = Number(localStorage.getItem(HOLD_KEY) || 0); }
    const remaining = Math.max(0, Math.floor((until - Date.now()) / 1000));
    const expired = remaining === 0;
    box.querySelector("[data-hold-running]").hidden = expired;
    box.querySelector("[data-hold-time]").hidden = expired;
    box.querySelector("[data-hold-expired]").hidden = !expired;
    box.querySelector("[data-hold-renew]").hidden = !expired;
    box.dataset.urgent = String(!expired && remaining <= 120);
    const t = box.querySelector("[data-hold-time]");
    t.textContent = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
    t.classList.toggle("text-violet", !expired && remaining <= 120); t.classList.toggle("text-graphite", expired || remaining > 120);
  }
}
customElements.define("falco-cart-drawer", FalcoCartDrawer);

/* ---------- Carrousel à accroche ---------- */
class FalcoCarousel extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector("[data-rail]");
    this.prev = this.querySelector("[data-prev]"); this.next = this.querySelector("[data-next]");
    this.bar = this.querySelector("[data-progress]"); this.controls = this.querySelector("[data-controls]");
    if (!this.rail) return;
    this.sync = this.sync.bind(this);
    this.rail.addEventListener("scroll", this.sync, { passive: true });
    new ResizeObserver(this.sync).observe(this.rail);
    this.prev?.addEventListener("click", () => this.nudge(-1));
    this.next?.addEventListener("click", () => this.nudge(1));
    this.sync();
  }
  sync() {
    const el = this.rail, max = el.scrollWidth - el.clientWidth;
    const scrollable = max > 4, atStart = el.scrollLeft <= 4, atEnd = el.scrollLeft >= max - 4;
    if (this.controls) this.controls.hidden = !scrollable;
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
    if (this.bar) this.bar.style.width = atEnd ? "100%" : atStart ? "34%" : "67%";
  }
  nudge(dir) {
    const first = this.rail.firstElementChild;
    const step = first ? first.getBoundingClientRect().width + 16 : this.rail.clientWidth * 0.8;
    this.rail.scrollBy({ left: dir * step, behavior: "smooth" });
  }
}
customElements.define("falco-carousel", FalcoCarousel);

/* ---------- Raccourci de configuration collant (mobile) ---------- */
class FalcoStickyCta extends HTMLElement {
  connectedCallback() {
    const hero = document.querySelector("main > .shopify-section:first-child, main > section");
    const zones = [...document.querySelectorAll("[data-cta-config]")];
    const visibles = new Set();
    const link = this.querySelector("a");
    const maj = () => {
      const bas = hero ? hero.getBoundingClientRect().bottom : 0;
      const on = bas < 0 && visibles.size === 0;
      this.setAttribute("aria-hidden", String(!on));
      this.classList.toggle("translate-y-0", on); this.classList.toggle("opacity-100", on);
      this.classList.toggle("translate-y-6", !on); this.classList.toggle("opacity-0", !on);
      link.classList.toggle("pointer-events-auto", on); link.tabIndex = on ? 0 : -1;
    };
    const obs = new IntersectionObserver((es) => { es.forEach((e) => (e.isIntersecting ? visibles.add(e.target) : visibles.delete(e.target))); maj(); }, { threshold: 0.2 });
    zones.forEach((z) => obs.observe(z));
    let tick = 0;
    window.addEventListener("scroll", () => { if (tick) return; tick = requestAnimationFrame(() => { tick = 0; maj(); }); }, { passive: true });
    maj();
  }
}
customElements.define("falco-sticky-cta", FalcoStickyCta);

/* ---------- Rail de catégories avec flèches ---------- */
class FalcoRail extends HTMLElement {
  connectedCallback() {
    const rail = this.querySelector("[data-rail]"), prev = this.querySelector("[data-prev]"), next = this.querySelector("[data-next]"), ctrl = this.querySelector("[data-controls]");
    if (!rail) return;
    const sync = () => { const max = rail.scrollWidth - rail.clientWidth; if (ctrl) ctrl.hidden = max <= 4; if (prev) prev.disabled = rail.scrollLeft <= 4; if (next) next.disabled = rail.scrollLeft >= max - 4; };
    rail.addEventListener("scroll", sync, { passive: true }); new ResizeObserver(sync).observe(rail);
    prev?.addEventListener("click", () => rail.scrollBy({ left: -420, behavior: "smooth" }));
    next?.addEventListener("click", () => rail.scrollBy({ left: 420, behavior: "smooth" }));
    sync();
  }
}
customElements.define("falco-rail", FalcoRail);

/* ---------- Panneau latéral générique (aide, filtres) ---------- */
document.addEventListener("click", (e) => {
  const opener = e.target.closest("[data-panel-open]");
  if (opener) {
    const p = document.getElementById(opener.dataset.panelOpen);
    if (p) { p.dataset.open = "true"; p.setAttribute("aria-hidden", "false"); lockScroll(true); setTimeout(() => p.querySelector("[data-panel-close]:not([tabindex='-1'])")?.focus(), 300); }
    return;
  }
  const closer = e.target.closest("[data-panel-close]");
  if (closer) {
    const p = closer.closest("[data-panel]");
    if (p && p.dataset.open === "true") { p.dataset.open = "false"; p.setAttribute("aria-hidden", "true"); lockScroll(false); }
  }
});
window.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  document.querySelectorAll("[data-panel][data-open=true]").forEach((p) => { p.dataset.open = "false"; p.setAttribute("aria-hidden", "true"); lockScroll(false); });
});

/* ---------- Fil « Voir plus / Voir moins » ---------- */
document.addEventListener("click", (e) => {
  const b = e.target.closest("[data-more-toggle]");
  if (!b) return;
  const panel = document.getElementById(b.getAttribute("aria-controls"));
  const open = panel.dataset.open === "true";
  panel.dataset.open = String(!open); b.setAttribute("aria-expanded", String(!open));
  b.querySelector("[data-more-label]").textContent = open ? b.dataset.labelMore : b.dataset.labelLess;
  b.querySelector("svg")?.classList.toggle("rotate-180", !open);
});

Object.assign(window.Falco, { formatMoney, lockScroll });
